import { genId } from "./utils.js";

// Un número que va a una columna numeric. Un campo de formulario que se borró
// queda en "" y PostgREST lo rechaza entero ("invalid input syntax for type
// numeric"): la cabecera ya se había insertado y las líneas no, y el documento
// quedaba en la nube SIN LÍNEAS. Pasó con RS-0228 (autoguardado con la casilla
// de bolsas vacía). Por eso ningún numérico sale de aquí como texto vacío.
export const num = v => (v === "" || v == null || Number.isNaN(Number(v))) ? null : Number(v);
export const num0 = v => { const n = num(v); return n == null ? 0 : n; };

export const CLOUD_TABLE_KEYS = new Set([
  "alertas_enviadas", "clientes", "proveedores", "productos", "empleados", "puc",
  "cotizaciones", "pedidos", "ocp", "remisiones", "facturas", "notas_credito",
  "inventario", "asientos", "resoluciones_dian", "nomina_liquidaciones", "activos_fijos",
  "cuentas_pagar", "cuentas_cobro", "requisiciones", "recepciones", "apartados"
]);

export const ENTITY_SCHEMA = {
  alertas_enviadas: {
    table: "alertas_enviadas",
    toRow: r => ({
      id: r.id, key: r.key, grupo: r.grupo || null,
      doc_tipo: r.docTipo || null, doc_id: r.docId || null, doc_numero: r.docNumero || null,
      tercero: r.tercero || null, linea: r.linea || null, fecha: r.fecha || null
    }),
    fromRow: row => ({
      id: row.id, key: row.key, grupo: row.grupo,
      docTipo: row.doc_tipo, docId: row.doc_id, docNumero: row.doc_numero,
      tercero: row.tercero, linea: row.linea, fecha: row.fecha
    })
  },
  clientes: {
    table: "clientes",
    toRow: r => ({
      id: r.id, nombre: r.nombre, nit: r.nit || null, contacto: r.contacto || null,
      contacto_cargo: r.contacto_cargo || null,
      // Cuál contacto se imprime en la cotización. Ver COMENTARIO de la columna:
      // -1 = el contacto inicial, 0..N = índice en _contactos, -2 = sin contacto a
      // propósito, null = todavía sin definir (lo que dispara el repaso retroactivo).
      contacto_imprimible: r.contacto_imprimible == null ? null : Number(r.contacto_imprimible),
      telefono: r.telefono || null, asesor_comercial: r.asesor_comercial || null, email: r.email || null,
      // Correo del BUZÓN DIAN del cliente, distinto del de contacto comercial:
      // la factura electrónica tiene que llegarle a quien la radica.
      email_fe: r.email_fe || null,
      direccion: r.direccion || null, ciudad: r.ciudad || null, dias_pago: r.dias_pago || null,
      forma_pago: r.forma_pago || null,
      tipo_persona: r.tipo_persona || null, tipo_documento: r.tipo_documento || null, dv: r.dv || null,
      regimen_fiscal: r.regimen_fiscal || null, ciudad_dian: r.ciudad_dian || null,
      responsable_iva: !!r.responsable_iva, actividad_economica: r.actividad_economica || null,
      retefuente: r.retefuente || 0, reteica: r.reteica || 0, reteiva: r.reteiva || 0,
      gran_contribuyente: !!r.gran_contribuyente, autorretenedor: !!r.autorretenedor,
      agente_retencion: !!r.agente_retencion
    }),
    fromRow: row => ({
      id: row.id, nombre: row.nombre, nit: row.nit, contacto: row.contacto,
      contacto_cargo: row.contacto_cargo, contacto_imprimible: row.contacto_imprimible,
      telefono: row.telefono, asesor_comercial: row.asesor_comercial, email: row.email,
      email_fe: row.email_fe,
      direccion: row.direccion, ciudad: row.ciudad, dias_pago: row.dias_pago,
      forma_pago: row.forma_pago,
      tipo_persona: row.tipo_persona, tipo_documento: row.tipo_documento, dv: row.dv,
      regimen_fiscal: row.regimen_fiscal, ciudad_dian: row.ciudad_dian,
      responsable_iva: row.responsable_iva, actividad_economica: row.actividad_economica,
      retefuente: row.retefuente, reteica: row.reteica, reteiva: row.reteiva,
      gran_contribuyente: row.gran_contribuyente, autorretenedor: row.autorretenedor,
      agente_retencion: row.agente_retencion
    }),
    childTables: [
      {
        field: "_contactos", table: "cliente_contactos", fk: "cliente_id",
        toRow: (item, posicion) => ({ nombre: item.nombre || null, cargo: item.cargo || null, telefono: item.telefono || null, email: item.email || null, posicion }),
        fromRow: row => ({ nombre: row.nombre, cargo: row.cargo, telefono: row.telefono, email: row.email })
      }
    ]
  },
  proveedores: {
    table: "proveedores",
    toRow: r => ({
      id: r.id, nombre: r.nombre, nit: r.nit || null, contacto: r.contacto || null, telefono: r.telefono || null,
      email: r.email || null, direccion: r.direccion || null, ciudad: r.ciudad || null, dias_pago: r.dias_pago || null,
      tipo_persona: r.tipo_persona || null, tipo_documento: r.tipo_documento || null, dv: r.dv || null,
      regimen_fiscal: r.regimen_fiscal || null, ciudad_dian: r.ciudad_dian || null, responsable_iva: !!r.responsable_iva,
      actividad_economica: r.actividad_economica || null, retefuente: r.retefuente || 0, reteica: r.reteica || 0, reteiva: r.reteiva || 0,
      gran_contribuyente: !!r.gran_contribuyente, autorretenedor: !!r.autorretenedor, agente_retencion: !!r.agente_retencion,
      // Persona natural que firma la declaración del art. 383 E.T. ("no tomaré
      // costos ni deducciones"). Decide si esa declaración se IMPRIME en su
      // cuenta de cobro: ponerla en la de alguien que no la firmó sería meterle
      // un juramento ajeno en un documento que va a firmar.
      declara_383: !!r.declara_383,
      desc_fin_tarjeta: r.desc_fin_tarjeta || 0, desc_fin_efectivo: r.desc_fin_efectivo || 0, desc_fin_transferencia: r.desc_fin_transferencia || 0
    }),
    fromRow: row => ({
      id: row.id, nombre: row.nombre, nit: row.nit, contacto: row.contacto, telefono: row.telefono,
      email: row.email, direccion: row.direccion, ciudad: row.ciudad, dias_pago: row.dias_pago,
      tipo_persona: row.tipo_persona, tipo_documento: row.tipo_documento, dv: row.dv,
      regimen_fiscal: row.regimen_fiscal, ciudad_dian: row.ciudad_dian, responsable_iva: row.responsable_iva,
      actividad_economica: row.actividad_economica, retefuente: row.retefuente, reteica: row.reteica, reteiva: row.reteiva,
      gran_contribuyente: row.gran_contribuyente, autorretenedor: row.autorretenedor, agente_retencion: row.agente_retencion,
      declara_383: row.declara_383,
      desc_fin_tarjeta: row.desc_fin_tarjeta, desc_fin_efectivo: row.desc_fin_efectivo, desc_fin_transferencia: row.desc_fin_transferencia
    }),
    childTables: [
      {
        field: "descuentos_financieros", table: "proveedor_descuentos_financieros", fk: "proveedor_id",
        toRow: (item, posicion) => ({ medio: item.medio || null, pct: item.pct || null, dias: item.dias || null, posicion }),
        fromRow: row => ({ medio: row.medio, pct: row.pct, dias: row.dias })
      }
    ]
  },
  productos: {
    table: "productos",
    toRow: r => ({
      id: r.id, nombre: r.nombre, referencia: r.referencia || null, categoria: r.categoria || null, unidad: r.unidad || null,
      // Código interno de Mathiz: TEXTO, para no perder los ceros de "00001".
      codigo_interno: r.codigo_interno || null,
      costo: num(r.costo), margen: num(r.margen), precio_venta: num(r.precio_venta), imagen: r.imagen || null,
      // La foto vive en el bucket y la ficha guarda solo su ruta. `imagen` se
      // conserva para las fichas que aún no se han migrado (ver imagenProducto.js).
      imagen_path: r.imagen_path || null,
      // Régimen de IVA del bien: gravado (art. 468), exento (477) o excluido (424).
      // Sin clasificar viaja como 'gravado' a la tarifa general, que es lo que
      // eran todos los productos antes de existir este campo.
      iva_tipo: r.iva_tipo || "gravado",
      iva_pct: num(r.iva_pct),
      // Un producto KIT se vende armado y no es inventariable: su receta está
      // en producto_kit_componentes y su stock se CALCULA con lo que hay de
      // sus partes. Ver src/core/kitCatalogo.js.
      es_kit: !!r.es_kit,
      costo_fecha: r._costo_fecha || null
    }),
    fromRow: row => ({
      id: row.id, nombre: row.nombre, referencia: row.referencia, categoria: row.categoria, unidad: row.unidad,
      codigo_interno: row.codigo_interno,
      costo: row.costo, margen: row.margen, precio_venta: row.precio_venta, imagen: row.imagen,
      imagen_path: row.imagen_path,
      iva_tipo: row.iva_tipo, iva_pct: row.iva_pct,
      es_kit: row.es_kit,
      _costo_fecha: row.costo_fecha
    }),
    childTables: [
      {
        field: "_componentesKit", table: "producto_kit_componentes", fk: "producto_id",
        toRow: (item, posicion) => ({
          articulo: item.articulo || null, cantidad: num(item.cantidad), posicion
        }),
        fromRow: row => ({ articulo: row.articulo, cantidad: row.cantidad })
      }
    ]
  },
  empleados: {
    table: "empleados",
    toRow: r => ({
      id: r.id, nombre: r.nombre, cedula: r.cedula || null, telefono: r.telefono || null, email: r.email || null,
      cargo: r.cargo || null, rango: r.rango || null, es_vendedor: !!r.es_vendedor && r.es_vendedor !== "" , fecha_cumpleanos: r.fecha_cumpleanos || null, genero: r.genero || null,
      eps: r.eps || null, arl: r.arl || null, fondo_pensiones: r.fondo_pensiones || null,
      banco: r.banco || null, cuenta_bancaria: r.cuenta_bancaria || null,
      salario_base: num(r.salario_base), fecha_ingreso: r.fecha_ingreso || null,
      tipo_contrato: r.tipo_contrato || null, nivel_riesgo_arl: r.nivel_riesgo_arl || null,
      periodicidad_pago: r.periodicidad_pago || null
    }),
    fromRow: row => ({
      id: row.id, nombre: row.nombre, cedula: row.cedula, telefono: row.telefono, email: row.email,
      cargo: row.cargo, rango: row.rango, es_vendedor: !!row.es_vendedor, fecha_cumpleanos: row.fecha_cumpleanos, genero: row.genero,
      eps: row.eps, arl: row.arl, fondo_pensiones: row.fondo_pensiones,
      banco: row.banco, cuenta_bancaria: row.cuenta_bancaria,
      salario_base: row.salario_base, fecha_ingreso: row.fecha_ingreso,
      tipo_contrato: row.tipo_contrato, nivel_riesgo_arl: row.nivel_riesgo_arl,
      periodicidad_pago: row.periodicidad_pago
    })
  },
  puc: {
    table: "puc",
    idField: "codigo",
    toRow: r => ({ codigo: r.codigo, nombre: r.nombre, naturaleza: r.naturaleza || null }),
    fromRow: row => ({ codigo: row.codigo, nombre: row.nombre, naturaleza: row.naturaleza })
  },
  cotizaciones: {
    table: "cotizaciones",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null, cliente_id: r.cliente_id || null,
      asesor_comercial: r.asesor_comercial || null, estado: r.estado || null,
      subtotal: num0(r.subtotal), iva: num0(r.iva), total: num0(r.total), notas: r.notas || null,
      fecha_vigencia: r.fecha_vigencia || null, contacto: r._contacto || null,
      // "¿A quién va dirigida?" ya contestada: sin persistirlo, cada edición
      // posterior volvía a preguntarlo.
      contacto_confirmado: !!r._contactoConfirmado,
      fecha_enviada: r._fecha_enviada || null,
      // Tercero NO registrado: la CT lleva encima sus datos (no hay ficha en
      // Maestros). Sin esta columna, al recargar se perdería el cliente.
      cliente_libre: r._clienteLibre || null,
      // Condiciones comerciales de la CT: sin estas columnas se perdían al
      // recargar (solo vivían en memoria de la sesión).
      tiempo_entrega: num(r.tiempo_entrega), lugar_entrega: r.lugar_entrega || null,
      condiciones_pago: r.condiciones_pago || null, forma_pago: r.forma_pago || null,
      dias_vigencia: (r._dias_vigencia === "" || r._dias_vigencia == null) ? null : r._dias_vigencia,
      origen: r._origen || null, convertido_a: r._convertedTo || null, locked: !!r._locked,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha, cliente_id: row.cliente_id,
      asesor_comercial: row.asesor_comercial, estado: row.estado,
      subtotal: row.subtotal, iva: row.iva, total: row.total, notas: row.notas,
      fecha_vigencia: row.fecha_vigencia, _contacto: row.contacto,
      _contactoConfirmado: !!row.contacto_confirmado,
      _fecha_enviada: row.fecha_enviada,
      _clienteLibre: row.cliente_libre,
      tiempo_entrega: row.tiempo_entrega, lugar_entrega: row.lugar_entrega,
      condiciones_pago: row.condiciones_pago, forma_pago: row.forma_pago,
      _dias_vigencia: row.dias_vigencia,
      _origen: row.origen, _convertedTo: row.convertido_a, _locked: row.locked,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre
    }),
    childTables: [
      {
        field: "items", table: "cotizaciones_items", fk: "cotizacion_id",
        toRow: (item, posicion) => ({
          articulo: item.articulo || null, descripcion: item.descripcion || null, referencia: item.referencia || null,
          cantidad: num(item.cantidad), impresion: item.impresion || null,
          precio_unitario: num(item.precio_unitario), total: num(item.total),
          costo_manual: num(item._costoManual), margen_manual: num(item._margenManual),
          // ¿El precio lo tecleó alguien? Entonces manda sobre el que saldría
          // del costo y el margen, también después de guardar.
          precio_manual: item._precioManual == null ? null : !!item._precioManual,
          // Costos propios de la línea (marcación, domicilio…): al convertir la
          // cotización en OP pasan a los costos asociados del documento.
          costos_linea: (item._costosLinea && item._costosLinea.length) ? item._costosLinea : null,
          // Escalas de cantidad: el mismo artículo cotizado a varias cantidades,
          // cada una con su precio. Reemplazan la cantidad única de la línea.
          escalas: (item._escalas && item._escalas.length) ? item._escalas : null,
          posicion
        }),
        fromRow: row => ({
          articulo: row.articulo, descripcion: row.descripcion, referencia: row.referencia,
          cantidad: row.cantidad, impresion: row.impresion,
          precio_unitario: row.precio_unitario, total: row.total,
          _costoManual: row.costo_manual, _margenManual: row.margen_manual,
          // Las líneas de antes de la marca (NULL): un precio guardado sin
          // margen fijado ES el precio que alguien dejó — se respeta. Solo las
          // que tienen margen fijado siguen saliendo del costo.
          // "Margen fijado" es el que de verdad fija el precio: entre 0 y 100.
          // Un 0 o un 100 guardados no fijan nada (margenFijadoLinea los
          // ignora), así que el precio de esa línea también es el que alguien dejó.
          _precioManual: row.precio_manual != null ? !!row.precio_manual
            : !(Number(row.margen_manual) > 0 && Number(row.margen_manual) < 100),
          _costosLinea: row.costos_linea || [],
          _escalas: row.escalas || []
        })
      }
    ]
  },
  pedidos: {
    table: "pedidos",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null, cliente_id: r.cliente_id || null,
      asesor_comercial: r.asesor_comercial || null, estado: r.estado || null,
      subtotal: num0(r.subtotal), iva: num0(r.iva), total: num0(r.total), notas: r.notas || null,
      // Condiciones comerciales y fechas de entrega de la OP: sin estas columnas
      // se perdían al recargar (solo vivían en memoria de la sesión).
      numero_oc_cliente: r.numero_oc_cliente || null, condiciones_envio: r.condiciones_envio || null,
      condiciones_pago: r.condiciones_pago || null, forma_pago: r.forma_pago || null,
      enviar_a: r.enviar_a || null, tiempo_entrega: num(r.tiempo_entrega), lugar_entrega: r.lugar_entrega || null,
      dias_vigencia: (r._dias_vigencia === "" || r._dias_vigencia == null) ? null : r._dias_vigencia,
      entregas: (r._entregas && r._entregas.length) ? r._entregas : null,
      // Anticipos del cliente sobre la OP. Vivían SOLO en el navegador: al
      // recargar desaparecían, aunque su asiento (crédito a 2710) sí quedaba
      // en la contabilidad — un anticipo contabilizado que nadie podía ver ni
      // cruzar con la factura.
      anticipos: (r._anticipos && r._anticipos.length) ? r._anticipos : null,
      fecha_enviada: r._fecha_enviada || null,
      origen: r._origen || null, convertido_a: r._convertedTo || null, locked: !!r._locked,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha, cliente_id: row.cliente_id,
      asesor_comercial: row.asesor_comercial, estado: row.estado,
      subtotal: row.subtotal, iva: row.iva, total: row.total, notas: row.notas,
      numero_oc_cliente: row.numero_oc_cliente, condiciones_envio: row.condiciones_envio,
      condiciones_pago: row.condiciones_pago, forma_pago: row.forma_pago,
      enviar_a: row.enviar_a, tiempo_entrega: row.tiempo_entrega, lugar_entrega: row.lugar_entrega,
      _dias_vigencia: row.dias_vigencia, _entregas: row.entregas || [],
      _anticipos: row.anticipos || [],
      _fecha_enviada: row.fecha_enviada,
      _origen: row.origen, _convertedTo: row.convertido_a, _locked: row.locked,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre
    }),
    childTables: [
      {
        field: "items", table: "pedidos_items", fk: "pedido_id",
        toRow: (item, posicion) => ({
          articulo: item.articulo || null, descripcion: item.descripcion || null, referencia: item.referencia || null,
          cantidad: num(item.cantidad), color: item.color || null, logo: item.logo || null,
          impresion: item.impresion || null, tinta: item.tinta || null,
          precio_unitario: num(item.precio_unitario), total: num(item.total), posicion
        }),
        fromRow: row => ({
          articulo: row.articulo, descripcion: row.descripcion, referencia: row.referencia,
          cantidad: row.cantidad, color: row.color, logo: row.logo, impresion: row.impresion, tinta: row.tinta,
          precio_unitario: row.precio_unitario, total: row.total
        })
      },
      {
        field: "_conceptosCosto", table: "pedidos_conceptos_costo", fk: "pedido_id",
        toRow: (item, posicion) => ({
          ccid: item._ccid || null, concepto: item.concepto || null, proveedor_nombre: item.proveedor_nombre || null,
          // Producto del catálogo al que corresponde el costo, si lo hay: es lo
          // que permite que la OCP generada lleve un artículo real (con su IVA y
          // su kardex) en vez de un texto libre.
          articulo: item.articulo || null,
          // Artículo de la OP al que pertenece el costo. Con `articulo` (el
          // producto que ES) forma el kit: al remisionar sale este producto en
          // lugar del artículo vendido, porque ese se arma al entregar.
          articulo_origen: item._articuloOrigen || null,
          cantidad: num(item.cantidad), valor_tercero: num(item.valor_tercero),
          ocp_id: item._ocpId || null, es_comision: !!item._esComision, fecha: item._fecha || null,
          vendedor: item._vendedor || null, posicion,
          // Destino confirmado del concepto (ocp | cxp | nada) y la CxP directa creada
          via: item._via || null, cxp_id: item._cxpId || null,
          // Y si esa orden de compra lleva IVA ("si" | "no" | vacío = según la
          // ficha del proveedor). Sin columna se perdía al recargar, y la
          // siguiente sincronización habría devuelto el IVA que se quitó.
          // Con destino "cuenta de cobro", si además se le manda la orden
          // informativa con el compromiso. (La columna `iva_ocp` quedó sin uso:
          // una orden de compra oficial lleva SIEMPRE el IVA que corresponda,
          // y la informativa va sin él por definición. Se deja en la tabla por
          // si hiciera falta mirar atrás; nadie la escribe ni la lee.)
          ocp_ademas: item._ocpAdemas || null
        }),
        fromRow: row => ({
          _ccid: row.ccid, concepto: row.concepto, proveedor_nombre: row.proveedor_nombre,
          articulo: row.articulo, _articuloOrigen: row.articulo_origen,
          cantidad: row.cantidad, valor_tercero: row.valor_tercero,
          _ocpId: row.ocp_id, _esComision: row.es_comision, _fecha: row.fecha, _vendedor: row.vendedor,
          _via: row.via, _cxpId: row.cxp_id, _ocpAdemas: row.ocp_ademas
        })
      }
    ]
  },
  ocp: {
    table: "ocp",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null, proveedor_id: r.proveedor_id || null,
      estado: r.estado || null, estado_pago: r.estado_pago || null,
      subtotal: num0(r.subtotal), iva: num0(r.iva), total: num0(r.total), notas: r.notas || null,
      origen: r._origen || null, convertido_a: r._convertedTo || null, locked: !!r._locked,
      from_costo: !!r._fromCosto,
      // Orden INFORMATIVA (serie OCI): el compromiso que se le manda al
      // proveedor. No se ingresa, no se causa y no se paga; solo se cierra
      // cuando cumple, y ese cierre queda con su fecha y su autor.
      informativa: !!r._informativa, cerrada: r._cerrada || null,
      // Movimientos de la OCP. Vivían SOLO en el navegador: al recargar (o en
      // otro equipo) una OCP ya causada volvía a aparecer como pendiente, y se
      // perdían recepciones y pagos. Todo dato de dinero tiene que viajar.
      procesado: r._procesado || null,
      facturas_contables: (r._facturasContables && r._facturasContables.length) ? r._facturasContables : null,
      recepciones: (r._recepciones && r._recepciones.length) ? r._recepciones : null,
      pagos: (r._pagos && r._pagos.length) ? r._pagos : null,
      pago: r._pago || null,
      // Ajustes de precio hechos sobre una OCP ya recibida (para cuadrarla con
      // la factura del proveedor) y la solicitud pendiente de autorización.
      ajustes_precio: (r._ajustesPrecio && r._ajustesPrecio.length) ? r._ajustesPrecio : null,
      ajuste_precios: r._ajustePrecios || null,
      asiento_compra: r._asientoCompra || null,
      doc_firmado: r._docFirmado || null,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha, proveedor_id: row.proveedor_id,
      estado: row.estado, estado_pago: row.estado_pago,
      subtotal: row.subtotal, iva: row.iva, total: row.total, notas: row.notas,
      _origen: row.origen, _convertedTo: row.convertido_a, _locked: row.locked,
      _fromCosto: row.from_costo,
      _informativa: row.informativa || undefined, _cerrada: row.cerrada || undefined,
      _procesado: row.procesado || undefined,
      _facturasContables: row.facturas_contables || [],
      _recepciones: row.recepciones || [],
      _pagos: row.pagos || [],
      _pago: row.pago || undefined,
      _ajustesPrecio: row.ajustes_precio || [],
      _ajustePrecios: row.ajuste_precios || null,
      _asientoCompra: row.asiento_compra,
      _docFirmado: row.doc_firmado || null,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre
    }),
    childTables: [
      {
        field: "items", table: "ocp_items", fk: "ocp_id",
        toRow: (item, posicion) => ({
          articulo: item.articulo || null, descripcion: item.descripcion || null, referencia: item.referencia || null,
          cantidad: num(item.cantidad), ubicacion: item.ubicacion || null,
          precio_unitario: num(item.precio_unitario), total: num(item.total), posicion,
          // El régimen de IVA de la línea viaja, como en factura y nota crédito.
          // Sin columna se perdía al recargar y la orden INFORMATIVA —cuyas
          // líneas nacen "excluido"— se imprimía con IVA (ver 2026-10-05).
          iva_tipo: item.iva_tipo || null, iva_pct: num(item.iva_pct),
          // El detalle que se teclea en la línea y que el PDF imprime debajo de
          // la descripción. Sin columna se perdía al recargar: OCI-0001 se mandó
          // al proveedor con color, logo e impresión y volvió pelada (2026-10-14).
          color: item.color || null, logo: item.logo || null, impresion: item.impresion || null,
          // El costo asociado del que nació la línea, para que la sincronización
          // la reconozca y conserve lo tecleado en vez de reescribirla.
          ccid: item._ccid || null,
          // Flujo piloto: el renglón de la requisición del que nació la línea.
          rq_linea: item._rqLinea || null
        }),
        fromRow: row => ({
          articulo: row.articulo, descripcion: row.descripcion, referencia: row.referencia,
          cantidad: row.cantidad, ubicacion: row.ubicacion,
          precio_unitario: row.precio_unitario, total: row.total,
          iva_tipo: row.iva_tipo, iva_pct: row.iva_pct,
          color: row.color, logo: row.logo, impresion: row.impresion,
          _ccid: row.ccid, _rqLinea: row.rq_linea || undefined
        })
      }
    ]
  },
  remisiones: {
    table: "remisiones",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null, cliente_id: r.cliente_id || null,
      asesor_comercial: r.asesor_comercial || null, estado: r.estado || null,
      subtotal: num0(r.subtotal), iva: num0(r.iva), total: num0(r.total), notas: r.notas || null,
      origen: r._origen || null, convertido_a: r._convertedTo || null, locked: !!r._locked,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null,
      factura_info: r._factura || null,
      // El descuento de inventario al completar la RC y el documento firmado
      // subido: sin columna, otro navegador no los veía — el kardex se
      // descontaba DOS veces al facturar y el escaneado "desaparecía".
      inventario_descontado: !!r._inventarioDescontado,
      doc_firmado: r._docFirmado || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha, cliente_id: row.cliente_id,
      asesor_comercial: row.asesor_comercial, estado: row.estado,
      subtotal: row.subtotal, iva: row.iva, total: row.total, notas: row.notas,
      _origen: row.origen, _convertedTo: row.convertido_a, _locked: row.locked,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre,
      _factura: row.factura_info,
      _inventarioDescontado: !!row.inventario_descontado,
      _docFirmado: row.doc_firmado || null
    }),
    childTables: [
      {
        field: "items", table: "remisiones_items", fk: "remision_id",
        toRow: (item, posicion) => ({
          articulo: item.articulo || null, descripcion: item.descripcion || null, referencia: item.referencia || null,
          cantidad: num(item.cantidad), bolsas: num(item.bolsas), observaciones: item.observaciones || null,
          precio_unitario: num0(item.precio_unitario), total: num0(item.total), posicion
        }),
        fromRow: row => ({
          articulo: row.articulo, descripcion: row.descripcion, referencia: row.referencia,
          cantidad: row.cantidad, bolsas: row.bolsas, observaciones: row.observaciones,
          precio_unitario: row.precio_unitario, total: row.total
        })
      }
    ]
  },
  facturas: {
    table: "facturas",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null, cliente_id: r.cliente_id || null,
      asesor_comercial: r.asesor_comercial || null, estado: r.estado || null, estado_cobro: r.estado_cobro || null,
      subtotal: num0(r.subtotal), iva: num0(r.iva), total: num0(r.total), notas: r.notas || null,
      fecha_vencimiento: r.fecha_vencimiento || null, condiciones_pago: r.condiciones_pago || null,
      origen: r._origen || null, convertido_a: r._convertedTo || null, locked: !!r._locked,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null,
      dataico: r._dataico || null, es_local: !!r._local, es_externa: !!r._externa,
      // Abonos y fecha de cobro: mismo caso que en la OCP, eran locales.
      pagos: (r._pagos && r._pagos.length) ? r._pagos : null,
      fecha_cobro: r.fecha_cobro || null,
      // Ids de los asientos de la factura (venta/cobro/anulación): son los
      // candados contra asientos duplicados y la llave de la reversa al
      // anular. Y las retenciones que practica el cliente, calculadas al
      // emitir: la cartera las necesita para saber el neto real por cobrar.
      asiento_venta: r._asientoVenta || null,
      asiento_cobro: r._asientoCobro || null,
      asiento_anulacion: r._asientoAnulacion || null,
      fecha_anulacion: r.fecha_anulacion || null,
      retenciones: r.retenciones || null,
      // Bienes o servicios: fija la cuantía mínima con la que se evalúan esas retenciones.
      concepto_retencion: r.concepto_retencion || null,
      // Documentos que cubre la factura cuando agrupa varias entregas.
      origenes: (r._origenes && r._origenes.length) ? r._origenes : null,
      // Facturada ANTES de entregar: el costo de ventas se registra con la
      // salida de la mercancía, no con la factura, y queda anotado aquí lo que
      // falta por costear.
      sin_entrega: !!r._sinEntrega,
      costo_pendiente: (r._costoPendiente && r._costoPendiente.length) ? r._costoPendiente : null,
      // Cada reenvío del correo al cliente, para que quede el rastro de a qué
      // dirección y cuándo se mandó (sin columna, se perdería en silencio).
      reenvios: (r._reenvios && r._reenvios.length) ? r._reenvios : null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha, cliente_id: row.cliente_id,
      asesor_comercial: row.asesor_comercial, estado: row.estado, estado_cobro: row.estado_cobro,
      subtotal: row.subtotal, iva: row.iva, total: row.total, notas: row.notas,
      fecha_vencimiento: row.fecha_vencimiento, condiciones_pago: row.condiciones_pago,
      _origen: row.origen, _convertedTo: row.convertido_a, _locked: row.locked,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre,
      _dataico: row.dataico, _local: row.es_local, _externa: row.es_externa,
      _pagos: row.pagos || [],
      fecha_cobro: row.fecha_cobro,
      _asientoVenta: row.asiento_venta,
      _asientoCobro: row.asiento_cobro,
      _asientoAnulacion: row.asiento_anulacion,
      fecha_anulacion: row.fecha_anulacion,
      retenciones: row.retenciones || null,
      concepto_retencion: row.concepto_retencion,
      _origenes: row.origenes || [],
      _sinEntrega: !!row.sin_entrega,
      _costoPendiente: row.costo_pendiente || [],
      _reenvios: row.reenvios || []
    }),
    childTables: [
      {
        field: "items", table: "facturas_items", fk: "factura_id",
        toRow: (item, posicion) => ({
          articulo: item.articulo || null, descripcion: item.descripcion || null, referencia: item.referencia || null,
          cantidad: num(item.cantidad), precio_unitario: num(item.precio_unitario), total: num(item.total),
          // El régimen de IVA queda CONGELADO en el documento fiscal: si el
          // producto se reclasifica después, lo ya emitido no cambia.
          iva_tipo: item.iva_tipo || null, iva_pct: num(item.iva_pct), posicion
        }),
        fromRow: row => ({
          articulo: row.articulo, descripcion: row.descripcion, referencia: row.referencia,
          cantidad: row.cantidad, precio_unitario: row.precio_unitario, total: row.total,
          iva_tipo: row.iva_tipo, iva_pct: row.iva_pct
        })
      }
    ]
  },
  notas_credito: {
    table: "notas_credito",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null, cliente_id: r.cliente_id || null,
      estado: r.estado || null, subtotal: num0(r.subtotal), iva: num0(r.iva), total: num0(r.total),
      notas: r.notas || null, motivo: r.motivo || null,
      origen: r._origen || null, convertido_a: r._convertedTo || null, locked: !!r._locked,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null,
      dataico: r._dataico || null,
      asiento_nc: r._asientoNC || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha, cliente_id: row.cliente_id,
      estado: row.estado, subtotal: row.subtotal, iva: row.iva, total: row.total,
      notas: row.notas, motivo: row.motivo,
      _origen: row.origen, _convertedTo: row.convertido_a, _locked: row.locked,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre,
      _dataico: row.dataico,
      _asientoNC: row.asiento_nc
    }),
    childTables: [
      {
        field: "items", table: "notas_credito_items", fk: "nota_credito_id",
        toRow: (item, posicion) => ({
          articulo: item.articulo || null, descripcion: item.descripcion || null, referencia: item.referencia || null,
          cantidad: num(item.cantidad), precio_unitario: num(item.precio_unitario), total: num(item.total),
          // El régimen de IVA queda CONGELADO en el documento fiscal: si el
          // producto se reclasifica después, lo ya emitido no cambia.
          iva_tipo: item.iva_tipo || null, iva_pct: num(item.iva_pct), posicion
        }),
        fromRow: row => ({
          articulo: row.articulo, descripcion: row.descripcion, referencia: row.referencia,
          cantidad: row.cantidad, precio_unitario: row.precio_unitario, total: row.total,
          iva_tipo: row.iva_tipo, iva_pct: row.iva_pct
        })
      }
    ]
  },
  inventario: {
    table: "inventario",
    idField: "articulo",
    toRow: r => ({
      articulo: r.articulo, stock: num0(r.stock), costo_promedio: num0(r.costo_promedio), valor_total: num0(r.valor_total)
    }),
    fromRow: row => ({
      articulo: row.articulo, stock: row.stock, costo_promedio: row.costo_promedio, valor_total: row.valor_total
    }),
    childTables: [
      {
        field: "_movimientos", table: "inventario_movimientos", fk: "articulo",
        toRow: (item, posicion) => ({
          id: item.id || genId(), tipo: item.tipo || null, fecha: item.fecha || null,
          cantidad: num(item.cantidad), costo_unitario: num(item.costo_unitario),
          costo_promedio_resultante: num(item.costo_promedio_resultante), stock_resultante: num(item.stock_resultante),
          valor_total: num(item.valor_total),
          origen_tipo: (item.origen && item.origen.tipo) || null, origen_id: (item.origen && item.origen.id) || null,
          origen_numero: (item.origen && item.origen.numero) || null,
          // La NOTA dice por qué se movió: "parte de Set escolar" cuando sale un
          // componente de un kit, "reversa por eliminación de RS-xxxx" cuando
          // una remisión entregada se borra. Sin columna sobrevivía hasta la
          // recarga y después el kardex volvía a no explicar nada.
          origen_nota: (item.origen && item.origen.nota) || null, posicion
        }),
        fromRow: row => ({
          id: row.id, tipo: row.tipo, fecha: row.fecha, cantidad: row.cantidad, costo_unitario: row.costo_unitario,
          costo_promedio_resultante: row.costo_promedio_resultante, stock_resultante: row.stock_resultante, valor_total: row.valor_total,
          origen: (row.origen_tipo || row.origen_id || row.origen_numero || row.origen_nota)
            ? { tipo: row.origen_tipo, id: row.origen_id, numero: row.origen_numero, nota: row.origen_nota || null } : null
        })
      }
    ]
  },
  asientos: {
    table: "asientos",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null, concepto: r.concepto || null, tipo: r.tipo || null,
      // Fecha real del documento cuando el asiento hubo de registrarse en un mes
      // posterior (el anterior ya estaba cerrado). Null si coinciden.
      fecha_documento: r.fecha_documento || null,
      origen_tipo: (r.origen && r.origen.tipo) || null, origen_id: (r.origen && r.origen.id) || null, origen_numero: (r.origen && r.origen.numero) || null,
      total_debito: num(r.totalDebito), total_credito: num(r.totalCredito), balanceado: r.balanceado ?? null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha, concepto: row.concepto, tipo: row.tipo,
      fecha_documento: row.fecha_documento,
      origen: (row.origen_tipo || row.origen_id || row.origen_numero) ? { tipo: row.origen_tipo, id: row.origen_id, numero: row.origen_numero } : null,
      totalDebito: row.total_debito, totalCredito: row.total_credito, balanceado: row.balanceado
    }),
    childTables: [
      {
        field: "movimientos", table: "asientos_movimientos", fk: "asiento_id",
        toRow: (item, posicion) => ({
          cuenta: item.cuenta || null, nombre_cuenta: item.nombre || null, descripcion: item.descripcion || null,
          debito: num0(item.debito), credito: num0(item.credito), tercero_id: item.tercero_id || null, posicion
        }),
        fromRow: row => ({
          cuenta: row.cuenta, nombre: row.nombre_cuenta, descripcion: row.descripcion,
          debito: row.debito, credito: row.credito, tercero_id: row.tercero_id
        })
      }
    ]
  },
  // CxP directa: un costo asociado a una OP (transporte, flete, servicio de un
  // tercero) que NO amerita orden de compra — va derecho a Cuentas por Pagar,
  // conservando su vínculo con la OP porque afecta el margen final. Se legaliza
  // después agrupándola en una cuenta de cobro.
  cuentas_pagar: {
    table: "cuentas_pagar",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null,
      tercero_nombre: r.tercero_nombre || null, tercero_cedula: r.tercero_cedula || null,
      // La ficha del tercero: de ella salen las tarifas de retención y la
      // identificación con la que se reporta la exógena.
      tercero_id: r.tercero_id || null,
      concepto: r.concepto || null, valor: num0(r.valor),
      // El desglose de lo retenido. `valor` es el BRUTO que la persona firma;
      // `neto` es lo que cobra. Sin guardarlo, un cambio posterior en la ficha
      // recalcularía la retención de una CxP ya causada.
      retefuente: num0(r.retefuente), retefuente_pct: num0(r.retefuente_pct),
      reteica: num0(r.reteica), reteica_pct: num0(r.reteica_pct),
      neto: num(r.neto),
      origen: r._origen || null, estado: r.estado || null,
      asiento_id: r._asientoId || null, cuenta_cobro_id: r._cuentaCobroId || null,
      pago: r._pago || null, asiento_pago_id: r._asientoPagoId || null,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha,
      tercero_nombre: row.tercero_nombre, tercero_cedula: row.tercero_cedula,
      tercero_id: row.tercero_id,
      concepto: row.concepto, valor: row.valor,
      retefuente: row.retefuente, retefuente_pct: row.retefuente_pct,
      reteica: row.reteica, reteica_pct: row.reteica_pct, neto: row.neto,
      _origen: row.origen, estado: row.estado,
      _asientoId: row.asiento_id, _cuentaCobroId: row.cuenta_cobro_id,
      _pago: row.pago, _asientoPagoId: row.asiento_pago_id,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre
    })
  },
  // Cuenta de cobro: agrupa varias CxP directas del MISMO tercero en un solo
  // documento imprimible con el que la persona legaliza y cobra sus servicios.
  cuentas_cobro: {
    table: "cuentas_cobro",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null,
      persona: r.persona || null, motivo: r.motivo || null,
      cxp_ids: (r.cxpIds && r.cxpIds.length) ? r.cxpIds : null, total: num0(r.total),
      // `total` es el bruto firmado; `neto` lo que se giró. El documento tiene
      // que poder reimprimirse años después con las mismas cifras.
      retefuente: num0(r.retefuente), reteica: num0(r.reteica), neto: num(r.neto),
      estado: r.estado || null, pago: r._pago || null, asiento_pago_id: r._asientoPagoId || null,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha,
      persona: row.persona, motivo: row.motivo,
      cxpIds: row.cxp_ids || [], total: row.total,
      retefuente: row.retefuente, reteica: row.reteica, neto: row.neto,
      estado: row.estado, _pago: row.pago, _asientoPagoId: row.asiento_pago_id,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre
    })
  },
  // Flujo piloto (fase 2): lo que el pedido le pide a compras. Lo ordenado de
  // cada renglón NO se guarda: se deriva de las OCP (ocp_items.rq_linea) y de
  // los costos de la OP (ver core/requisicion.js).
  requisiciones: {
    table: "requisiciones",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null,
      pedido_id: r.pedido_id || null, pedido_numero: r.pedido_numero || null, cliente_id: r.cliente_id || null,
      estado: r.estado || null, notas: r.notas || null,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha,
      pedido_id: row.pedido_id, pedido_numero: row.pedido_numero, cliente_id: row.cliente_id,
      estado: row.estado, notas: row.notas,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre
    }),
    childTables: [
      {
        field: "items", table: "requisiciones_items", fk: "requisicion_id",
        toRow: (item, posicion) => ({
          lid: item.lid || null, tipo: item.tipo || null, articulo: item.articulo || null,
          articulo_origen: item.articulo_origen || null, concepto: item.concepto || null, ccid: item.ccid || null,
          cantidad: num(item.cantidad), costo: num(item.costo), proveedor_nombre: item.proveedor_nombre || null,
          via: item.via || null, cerrada: !!item.cerrada, motivo_cierre: item.motivo_cierre || null, posicion
        }),
        fromRow: row => ({
          lid: row.lid, tipo: row.tipo, articulo: row.articulo, articulo_origen: row.articulo_origen,
          concepto: row.concepto, ccid: row.ccid, cantidad: row.cantidad, costo: row.costo,
          proveedor_nombre: row.proveedor_nombre, via: row.via, cerrada: !!row.cerrada, motivo_cierre: row.motivo_cierre
        })
      }
    ]
  },
  // Flujo piloto (fase 3): la recepción (RP) como documento. La mercancía
  // entra por la puerta del ingreso de siempre; aquí queda el reparto a los
  // pedidos (`lineas[].destinos`) y quién ya vio el aviso de que llegó.
  recepciones: {
    table: "recepciones",
    toRow: r => ({
      id: r.id, numero: r.numero || null, fecha: r.fecha || null,
      ocp_id: r.ocp_id || null, ocp_numero: r.ocp_numero || null, proveedor_id: r.proveedor_id || null,
      pedido_id: r.pedido_id || null,
      lineas: (r.lineas && r.lineas.length) ? r.lineas : null,
      avisos_vistos: (r.avisos_vistos && r.avisos_vistos.length) ? r.avisos_vistos : null,
      notas: r.notas || null,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, fecha: row.fecha,
      ocp_id: row.ocp_id, ocp_numero: row.ocp_numero, proveedor_id: row.proveedor_id,
      pedido_id: row.pedido_id, lineas: row.lineas || [], avisos_vistos: row.avisos_vistos || [],
      notas: row.notas, _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre
    })
  },
  // Flujo piloto (fase 3): mercancía de la bodega reservada para un pedido,
  // sin vencimiento. Lo vigente se deriva (core/recepcion.js).
  apartados: {
    table: "apartados",
    toRow: r => ({
      id: r.id, fecha: r.fecha || null, pedido_id: r.pedido_id || null, pedido_numero: r.pedido_numero || null,
      articulo: r.articulo || null, cantidad: num(r.cantidad), origen: r.origen || null, liberado: r.liberado || null,
      creado_por: r._creadoPor || null, creado_por_nombre: r._creadoPorNombre || null
    }),
    fromRow: row => ({
      id: row.id, fecha: row.fecha, pedido_id: row.pedido_id, pedido_numero: row.pedido_numero,
      articulo: row.articulo, cantidad: row.cantidad, origen: row.origen, liberado: row.liberado || null,
      _creadoPor: row.creado_por, _creadoPorNombre: row.creado_por_nombre
    })
  },
  resoluciones_dian: {
    table: "resoluciones_dian",
    toRow: r => ({
      id: r.id, numero_resolucion: r.numero_resolucion || null, fecha_resolucion: r.fecha_resolucion || null,
      prefijo: r.prefijo || null, rango_desde: num(r.rango_desde), rango_hasta: num(r.rango_hasta),
      fecha_desde: r.fecha_desde || null, fecha_hasta: r.fecha_hasta || null,
      activa: r.activa !== false, notas: r.notas || null
    }),
    fromRow: row => ({
      id: row.id, numero_resolucion: row.numero_resolucion, fecha_resolucion: row.fecha_resolucion,
      prefijo: row.prefijo, rango_desde: row.rango_desde, rango_hasta: row.rango_hasta,
      fecha_desde: row.fecha_desde, fecha_hasta: row.fecha_hasta,
      activa: row.activa, notas: row.notas
    })
  },
  nomina_liquidaciones: {
    table: "nomina_liquidaciones",
    toRow: r => ({
      id: r.id, numero: r.numero || null, tipo_periodo: r.tipo_periodo || null,
      periodo_desde: r.periodo_desde || null, periodo_hasta: r.periodo_hasta || null,
      fecha_pago: r.fecha_pago || null, estado: r.estado || null,
      detalle_empleados: r.detalle_empleados || [], asiento_id: r.asiento_id || null
    }),
    fromRow: row => ({
      id: row.id, numero: row.numero, tipo_periodo: row.tipo_periodo,
      periodo_desde: row.periodo_desde, periodo_hasta: row.periodo_hasta,
      fecha_pago: row.fecha_pago, estado: row.estado,
      detalle_empleados: row.detalle_empleados || [], asiento_id: row.asiento_id
    })
  },
  activos_fijos: {
    table: "activos_fijos",
    toRow: r => ({
      id: r.id, codigo: r.codigo || null, nombre: r.nombre || null, categoria: r.categoria || null,
      cuenta_activo: r.cuenta_activo || null, fecha_compra: r.fecha_compra || null,
      costo_adquisicion: r.costo_adquisicion || 0, valor_residual: r.valor_residual || 0,
      vida_util_contable_meses: r.vida_util_contable_meses || 0, vida_util_fiscal_meses: r.vida_util_fiscal_meses || null,
      metodo: r.metodo || "linea_recta", turnos_adicionales: r.turnos_adicionales || false,
      depreciar_menor_cuantia: r.depreciar_menor_cuantia || false,
      responsable: r.responsable || null, ubicacion: r.ubicacion || null,
      estado: r.estado || "activo", fecha_baja: r.fecha_baja || null,
      motivo_baja: r.motivo_baja || null, valor_venta: r.valor_venta || null,
      depreciaciones: r.depreciaciones || []
    }),
    fromRow: row => ({
      id: row.id, codigo: row.codigo, nombre: row.nombre, categoria: row.categoria,
      cuenta_activo: row.cuenta_activo, fecha_compra: row.fecha_compra,
      costo_adquisicion: row.costo_adquisicion, valor_residual: row.valor_residual,
      vida_util_contable_meses: row.vida_util_contable_meses, vida_util_fiscal_meses: row.vida_util_fiscal_meses,
      metodo: row.metodo, turnos_adicionales: row.turnos_adicionales,
      depreciar_menor_cuantia: row.depreciar_menor_cuantia,
      responsable: row.responsable, ubicacion: row.ubicacion,
      estado: row.estado, fecha_baja: row.fecha_baja,
      motivo_baja: row.motivo_baja, valor_venta: row.valor_venta,
      depreciaciones: row.depreciaciones || []
    })
  }
};
